'use client'

import { Suspense, useEffect, useMemo } from 'react'
import { cn } from '@/lib/css-utils'
import { useForceLogin } from '@/modules/salary/common/use-force-login'
import { useSalaries } from '@/modules/salary/common/use-salaries'
import { SalaryDetails } from '@/modules/salary/details/details'
import { SalaryFilters } from '@/modules/salary/filters/filters'
import { AveragesHistogram } from '@/modules/salary/histogram/averages-histogram'
import { SalaryEmpty } from '@/modules/salary/table/empty'
import { SalaryTable } from '@/modules/salary/table/table'
import { SalaryAlerts } from './table/salary-alerts'
import { useSalaryDetails } from '@/modules/salary/details/use-salary-details'
import {
  SALARY_ROW_SELECTOR,
  SalaryVisibility,
} from '@/modules/salary/table/constants'
import cookies from 'js-cookie'
import { IS_FROM_ONBOARDING_PARAM_NAME } from '../surveys/configs/onboarding/constants'
import { AnimatePresence } from 'framer-motion'
import { useSalaryDetailsNavigation } from '@/modules/salary/details/use-salary-details-navigation'
import {
  SalaryRestrictionType,
  useSalaryTableStore,
} from '@/modules/salary/salary-table-context'
import { useShareTracking } from '@/modules/salary/analytics/use-share-tracking'
import { ShareSalaryV2 } from './share/share-salary-v2'
import { SortRow } from './sort-row/sort-row'
import { ConfettiPage } from '@/components/confetti-page'
import { SalaryFilterParam } from './constants'
import { useSearchParams } from 'next/navigation'

import { GiveToGetModalManager } from './common/give-to-get-modal-manager'
import { SeoStructuredModules } from './seo-structured-modules'
import { usePostHog } from 'posthog-js/react'
import { SalaryHistogramResponse } from '@/lib/generated/graphql-types'

interface SalaryProps {
  hasPublicLimitCookie: boolean
  didComeFromOnboarding: boolean
  isAuthenticated: boolean
  showTableRowHint?: boolean
  histogramData?: SalaryHistogramResponse
}

export function SalaryWrapper({
  hasPublicLimitCookie,
  didComeFromOnboarding,
  isAuthenticated,
  showTableRowHint,
  histogramData,
}: SalaryProps) {
  const { countPublicInteraction, publicInteractionLimitReached } =
    useForceLogin({
      hasPublicLimitCookie,
      isAuthenticated,
    })

  const setSalaryRestriction = useSalaryTableStore(
    (state) => state.setSalaryRestriction,
  )

  const { data } = useSalaries()
  const posthog = usePostHog()
  const searchParams = useSearchParams()
  const salaryIdParam = searchParams.get(SalaryFilterParam.salaryId)
  const salaries = useMemo(() => data?.searchSalary?.data ?? [], [data])
  const meta = data?.searchSalary?.meta
  const hasSalaries = salaries.length > 0
  const { salary } = useSalaryDetails({ salaries })
  useSalaryDetailsNavigation({ salaries })
  const isDetailsOpen = Boolean(salary)
  const { trackShare } = useShareTracking()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    posthog.capture('salary_search_result', {
      location: window.location.href,
      salaryCount: data?.searchSalary?.meta?.totalCount,
    })
  }, [posthog, data?.searchSalary?.meta?.totalCount])

  useEffect(() => {
    if (didComeFromOnboarding) {
      cookies.remove(IS_FROM_ONBOARDING_PARAM_NAME)
    }
  }, [didComeFromOnboarding])

  useEffect(() => {
    const _salary = salary || salaries[0]
    // only signed out scenario doesn't have any restriction, but we need to check
    // the interaction limits instead.
    if (
      _salary?.visibility !== SalaryVisibility.Restricted &&
      !publicInteractionLimitReached
    ) {
      setSalaryRestriction(null)
      return
    }
    const restriction = publicInteractionLimitReached
      ? SalaryRestrictionType.SignedOut
      : _salary.visibilityReason
    setSalaryRestriction(restriction as SalaryRestrictionType)
  }, [salary, salaries, publicInteractionLimitReached, setSalaryRestriction])

  useEffect(() => {
    if (salaryIdParam) {
      requestAnimationFrame(() => {
        document
          .querySelector(`#${SALARY_ROW_SELECTOR}${salaryIdParam}`)
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          })
      })
    }
  }, [salaryIdParam])

  return (
    <>
      <SeoStructuredModules />
      <ConfettiPage
        shouldExplode={didComeFromOnboarding && salaries.length > 0}
      />
      <div className="w-full flex flex-col grow md:px-default px-none">
        <div className="max-w-screen-2xl w-full mx-auto">
          <div className="py-3 flex justify-between md:px-0 px-4">
            <SalaryFilters
              isAuthenticated={isAuthenticated}
              countPublicInteraction={countPublicInteraction}
            />
            <ShareSalaryV2
              className="hidden md:flex ml-2"
              isAuthenticated={isAuthenticated}
              onMouseDown={() => trackShare('page', undefined, salary)}
            />
          </div>
          <div
            className={cn('flex', {
              'gap-0 md:gap-4': isDetailsOpen,
            })}
          >
            {/* Main content */}

            <div className="transition-all duration-200 overflow-y-auto grow flex flex-col h-auto">
              {hasSalaries && (
                <div
                  className={cn('flex transition-all duration-300', {
                    hidden: isDetailsOpen,
                  })}
                >
                  <AveragesHistogram
                    isAuthenticated={isAuthenticated}
                    histogramData={histogramData}
                  />
                </div>
              )}
              <AnimatePresence initial={false}>
                <SalaryAlerts
                  hasReachedPublicLimit={publicInteractionLimitReached}
                  hasSalaries={hasSalaries}
                  isAuthenticated={isAuthenticated}
                />
              </AnimatePresence>
              <SortRow
                isAuthenticated={isAuthenticated}
                countPublicInteraction={countPublicInteraction}
                hasTrendsResults={Boolean(histogramData?.hasTrends)}
              />
              {hasSalaries ? (
                <SalaryTable
                  salaries={salaries}
                  salaryMeta={meta}
                  hasReachedLimit={publicInteractionLimitReached}
                  countPublicInteraction={countPublicInteraction}
                  isAuthenticated={isAuthenticated}
                  showTableRowHint={showTableRowHint}
                />
              ) : (
                <SalaryEmpty isAuthenticated={isAuthenticated} />
              )}

              {/* No longer used - API has no real data at this point 3/26/2025 */}
              {/* <FeaturedEmployers
              isAuthenticated={isAuthenticated}
            /> */}
            </div>
            {/* Side panel */}
            <div
              className={cn(
                'overflow-y-auto sticky top-16 mb-4 rounded-lg md:bg-background-primary bg-transparent',
                'h-auto md:transition-all md:duration-200 shrink-0',
                {
                  'md:w-1/3 md:h-[calc(100vh-5rem)]': isDetailsOpen,
                  'md:w-0': !isDetailsOpen,
                },
              )}
            >
              <SalaryDetails
                salaries={salaries}
                salaryMeta={meta}
                isOpen={isDetailsOpen}
                isAuthenticated={isAuthenticated}
                countPublicInteraction={countPublicInteraction}
              />
            </div>
          </div>
        </div>
        <Suspense fallback={null}>
          <GiveToGetModalManager
            didComeFromOnboarding={didComeFromOnboarding}
            isAuthenticated={isAuthenticated}
          />
        </Suspense>
      </div>
    </>
  )
}
